import { useEffect, useRef, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { isMobile } from 'react-device-detect'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import {
  ErrorResponseDto,
  EstimateOrderRequestDtoV1,
  EstimateOrderResponseDtoV1ShippingsItem,
} from '@/apis/rest/generated/types'
import { LayoutHeader } from '@/components/domains/layouts'
import { MileageBenefitSection } from '@/components/domains/order2/mileage-benefit-section'
import { MileageSection } from '@/components/domains/order2/mileage-section'
import { OrderCouponSection } from '@/components/domains/order2/order-coupon-section'
import { OrderErrorModals } from '@/components/domains/order2/order-error-modals'
import { OrderPageHeader } from '@/components/domains/order2/order-page-header'
import { OrderPaymentSection } from '@/components/domains/order2/order-payment-section'
import { PayMethodSection } from '@/components/domains/order2/pay-method-section'
import { PaymentInfoSection } from '@/components/domains/order2/payment-info-section'
import { ProductListSection } from '@/components/domains/order2/product-list-section'
import { ShipmentCouponSection } from '@/components/domains/order2/shipment-coupon-section'
import { ShippingAddressSection } from '@/components/domains/order2/shipping-address-section'
import { UserInfoSection } from '@/components/domains/order2/user-info-section'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import ROUTES from '@/constants/legacy/constRoutes'
import { PaymentMethodTypeEnum } from '@/constants/payment-method-type.enum'
import useOrderPageEventTracker from '@/containers/event-tracker/order.event-tracker'
import { useCustomRouter } from '@/containers/hooks'
import { OrderFormSchema, OrderFormType, OrderItem } from '@/containers/hooks/order/order-form-schema'
import { useOrderNew } from '@/containers/hooks/order/useOrderNew'
import { usePostEstimateOrder } from '@/containers/order/post-estimate-order.hook'
import { UserContainer } from '@/containers/users/UserContainer'
import { ErrorType } from '@/plugins/rest-api-v4'
import { doSetOrderPageCouponApplyMessage } from '@/stores/reduxData'
import { doSetGlobalNoticeModal, doSetGlobalToastContent } from '@/stores/reduxUI'
import { RootState } from '@/stores/store'
import { Base64ConverterUtil } from '@/utils/base64-converter.util'
import { isErrorType } from '@/utils/is-response-error-type'

const NaverPayMinimumPaymentAmount = 100

const defaultValues = {
  coupon: undefined,
  customerAddress: {
    basicAddress: '',
    customerTemplateAddress: '',
    detailedAddress: '',
    isDefault: false,
    message: '',
    mobileNumber: '',
    name: '',
    postNumber: '',
    templateName: '',
  },
  isUseAllMileage: false,
  mileage: '0',
  payMethodType: PaymentMethodTypeEnum.TossPay,
  shippings: [
    {
      shippingPresetId: 0,
      shippingCoupon: undefined,
      orderItems: [
        {
          coupon: undefined,
          couponId: 0,
          productOption: '',
          productOptionId: 0,
          productPromotion: '',
          productPromotionId: 0,
          quantity: 0,
        },
      ],
    },
  ],
}

const makeShippingFormData = (shippings: EstimateOrderResponseDtoV1ShippingsItem[]): OrderFormType['shippings'] => {
  return shippings?.map((shipping) => {
    const orderItems: OrderItem[] =
      shipping.orderItems?.map((orderItem) => {
        const tempProductCoupon = orderItem.productCoupons?.find((coupon) => coupon.isApplied)
        return {
          coupon: Base64ConverterUtil.convertNumberIdToBase64ByKey({ key: 'CouponType', id: tempProductCoupon?.id }),
          couponId: tempProductCoupon?.id,
          productOption: Base64ConverterUtil.convertNumberIdToBase64ByKey({
            key: 'ProductOptionType',
            id: orderItem.productOptionId,
          }),
          productOptionId: orderItem.productOptionId,
          productPromotion: orderItem.productPromotionId
            ? Base64ConverterUtil.convertNumberIdToBase64ByKey({
                key: 'ProductPromotionType',
                id: orderItem.productPromotionId,
              })
            : undefined,
          productPromotionId: orderItem.productPromotionId || undefined,
          quantity: orderItem.quantity,
        }
      }) || []
    const appliedShippingCoupon = shipping.shippingCoupons?.find((coupon) => coupon.isApplied)
    return {
      shippingPresetId: shipping.shippingPresetId,
      shippingCoupon: Base64ConverterUtil.convertNumberIdToBase64ByKey({
        key: 'CouponType',
        id: appliedShippingCoupon?.id,
      }),
      orderItems,
    }
  })
}

/**
 * 배송지별 적용된 배송비 쿠폰 할인액의 총합.
 * 응답의 shippingCouponDiscountAmount 필드는 미사용(항상 0)이라, 배송지별 applied 쿠폰의 appliedDiscountAmount 를 직접 합산한다.
 */
const getTotalShippingCouponDiscountAmount = (shippings: EstimateOrderResponseDtoV1ShippingsItem[]): number => {
  return (
    shippings?.reduce((sum, shipping) => {
      const appliedShippingCoupon = shipping.shippingCoupons?.find((coupon) => coupon.isApplied)
      return sum + (appliedShippingCoupon?.appliedDiscountAmount ?? 0)
    }, 0) ?? 0
  )
}

interface OrderProps {
  estimatedOrderRequest: EstimateOrderRequestDtoV1
}

export const Order = ({ estimatedOrderRequest }: OrderProps) => {
  const dispatch = useDispatch()

  const { basePath, asPath, push, query, isReady } = useCustomRouter()
  const { trackViewOrder } = useOrderPageEventTracker()

  const [error, setError] = useState<ErrorType<ErrorResponseDto> | undefined>(undefined)

  const { postEstimateOrder } = usePostEstimateOrder()
  const { isPaymentInProgress, hasOrderError, createOrderNew, onPaymentMethodSelect, getPaymentMethod } = useOrderNew()

  const methods = useForm<OrderFormType>({
    resolver: zodResolver(OrderFormSchema),
    mode: 'onSubmit',
    defaultValues,
  })

  const estimatedOrderResponse = useSelector(
    (rootState: RootState) => rootState.reduxDataReducers.estimatedOrderResponse
  )

  const orderPageCouponApplyMessage = useSelector(
    (rootState: RootState) => rootState.reduxDataReducers.orderPageCouponApplyMessage
  )

  const [isPayco, setIsPayco] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const wasPaymentInProgressRef = useRef(false)

  useEffect(() => {
    if (isPaymentInProgress) {
      wasPaymentInProgressRef.current = true
    } else if (wasPaymentInProgressRef.current) {
      wasPaymentInProgressRef.current = false
      setIsSubmitting(false)
    }
  }, [isPaymentInProgress])

  useEffect(() => {
    if (hasOrderError) {
      setIsSubmitting(false)
    }
  }, [hasOrderError])

  const handleInvalid = () => {
    const customerAddressError = methods.formState.errors?.customerAddress
    if (customerAddressError?.customerTemplateAddress || customerAddressError?.basicAddress) {
      dispatch(
        doSetGlobalToastContent({
          content: '배송지를 입력해 주세요.',
        })
      )
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }
    if (customerAddressError?.mobileNumber) {
      dispatch(
        doSetGlobalNoticeModal({
          text: '배송 받으실 분의\n올바른 휴대폰번호를 입력해주세요.',
          buttonType: 'ACTION',
          visible: true,
          onOk: () =>
            push({
              pathname: ROUTES.DELIVERY_ADDRESSES.UPDATE,
              query: {
                customerTemplateAddressId: methods.getValues('customerAddress.customerTemplateAddress'),
              },
            }),
        })
      )
    }
  }

  const handleClickSubmit = (formData: OrderFormType) => {
    if (isSubmitting) {
      return
    }
    setIsSubmitting(true)

    if (
      formData.amount <= NaverPayMinimumPaymentAmount &&
      formData.payMethodType.toLowerCase() === PaymentMethodTypeEnum.NaverPay
    ) {
      dispatch(
        doSetGlobalNoticeModal({
          text: '100원 미만의 결제는 네이버페이가 아닌 다른 결제수단을 이용해주세요.',
          buttonType: 'ACTION',
          visible: true,
        })
      )
      setIsSubmitting(false)
      return
    }

    createOrderNew(formData)

    // todo payco iframeYn 미지원으로 임시 pushState, 추후 제거 20220801 bale
    if (getPaymentMethod() === PaymentMethodTypeEnum.Payco && isMobile) {
      const currentPath = `${basePath}${asPath}`
      window.history.pushState(undefined, '', `${currentPath}`)
      window.addEventListener('popstate', () => {
        location.reload()
      })
      setIsPayco(true)
    }
  }

  const handleClickErrorModalOk = () => {
    setError(undefined)
  }

  // form data 설정
  useEffect(() => {
    if (!estimatedOrderResponse) {
      return
    }

    const orderCoupon = estimatedOrderResponse.orderCoupons?.find((coupon) => coupon.isApplied)
    methods.setValue(
      'coupon',
      Base64ConverterUtil.convertNumberIdToBase64ByKey({ key: 'CouponType', id: orderCoupon?.id })
    )
    methods.setValue('couponId', orderCoupon?.id)
    methods.setValue('amount', estimatedOrderResponse.amount)
    methods.setValue('mileage', estimatedOrderResponse.mileageDiscountAmount.toString())

    const shippings = makeShippingFormData(estimatedOrderResponse.shippings)
    methods.setValue('shippings', shippings)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimatedOrderResponse])

  useEffect(() => {
    const fetchEstimateOrder = async (payload: EstimateOrderRequestDtoV1) => {
      const result = await postEstimateOrder(payload)
      if (!result) {
        return
      }

      if (isErrorType(result)) {
        setError(result)
        return
      }

      if (orderPageCouponApplyMessage) {
        dispatch(doSetGlobalToastContent({ content: orderPageCouponApplyMessage }))
        dispatch(doSetOrderPageCouponApplyMessage(''))
      }

      trackViewOrder({
        estimateOrder: result,
        routerParam: { isReady, query },
      })
    }

    fetchEstimateOrder(estimatedOrderRequest)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimatedOrderRequest])

  if (isPayco) {
    return (
      <>
        <LayoutHeader />
        <StyledPaycoBack>
          페이지 로딩 중입니다. <br />
          계속 지연될 경우 뒤로가기 버튼을 눌러 주세요.
        </StyledPaycoBack>
      </>
    )
  }

  return (
    <UserContainer.Provider>
      {estimatedOrderResponse && (
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleClickSubmit, handleInvalid)}>
            <>
              <OrderPageHeader />
              {/* 주문 상품 섹션 */}
              <ProductListSection
                shippings={estimatedOrderResponse.shippings}
                countOfOrderItem={estimatedOrderResponse.countOfOrderItem}
              />
              <StyledDivider />
              {/* 주문자 정보 섹션 */}
              <UserInfoSection />
              <StyledDivider />
              {/* 배송지 정보 섹션 */}
              <ShippingAddressSection />
              <StyledDivider />
              {/* 주문 쿠폰 섹션 */}
              <OrderCouponSection
                countOfAvailableOrderCoupon={estimatedOrderResponse.countOfAvailableOrderCoupon}
                orderCoupons={estimatedOrderResponse.orderCoupons}
                isPartialOrderCouponDiscount={estimatedOrderResponse.isPartialOrderCouponDiscount}
                productSaleAmount={estimatedOrderResponse.productSaleAmount}
              />
              <StyledDivider />
              <ShipmentCouponSection
                shippingIndex={estimatedOrderResponse.shippings.findIndex(
                  (shipping) => shipping.hasAvailableShippingCoupon
                )}
                countOfAvailableShippingCoupon={
                  estimatedOrderResponse.shippings.find((shipping) => shipping.hasAvailableShippingCoupon)
                    ?.countOfAvailableShippingCoupon ?? 0
                }
                isPartialOrderCouponDiscount={estimatedOrderResponse.isPartialOrderCouponDiscount}
                shippingCoupons={
                  estimatedOrderResponse.shippings.find((shipping) => shipping.hasAvailableShippingCoupon)
                    ?.shippingCoupons
                }
                productSaleAmount={estimatedOrderResponse.productSaleAmount}
              />
              <StyledDivider />
              {/* 적립금 섹션 */}
              <MileageSection
                availableMileage={estimatedOrderResponse.availableMileage}
                mileageDiscountAmount={estimatedOrderResponse.mileageDiscountAmount}
                availableMileagePercentage={estimatedOrderResponse.availableMileagePercentage}
                isUseAllMileage={estimatedOrderResponse.isUseAllMileage}
                mileagePolicyContent={estimatedOrderResponse.mileagePolicyContent}
              />
              <StyledDivider />
              {/* 결제 수단 섹션 */}
              <PayMethodSection onPaymentMethodSelect={onPaymentMethodSelect} getPaymentMethod={getPaymentMethod} />
              <StyledDivider />
              {/* 결제정보 섹션 */}
              <PaymentInfoSection
                isMembership={estimatedOrderResponse.isMembership}
                productOriginalAmount={estimatedOrderResponse.productOriginalAmount}
                shippingAmount={estimatedOrderResponse.shippingAmount}
                shippingCouponDiscountAmount={getTotalShippingCouponDiscountAmount(estimatedOrderResponse.shippings)}
                productPromotionCustomerDiscountAmount={estimatedOrderResponse.productPromotionCustomerDiscountAmount}
                productCouponDiscountAmount={estimatedOrderResponse.productCouponDiscountAmount}
                orderCouponDiscountAmount={estimatedOrderResponse.orderCouponDiscountAmount}
                mileageDiscountAmount={estimatedOrderResponse.mileageDiscountAmount}
                amount={estimatedOrderResponse.amount}
                discountAmountWithoutMileage={estimatedOrderResponse.discountAmountWithoutMileage}
              />
              <StyledDivider />
              {/* 적립금 혜택 섹션 */}
              <MileageBenefitSection
                isMembership={estimatedOrderResponse.isMembership}
                expectedMileage={estimatedOrderResponse.expectedMileage}
                expectedOrderConfirmMileageForRegular={estimatedOrderResponse.expectedOrderConfirmMileageForRegular}
                expectedImageReviewMileage={estimatedOrderResponse.expectedImageReviewMileage}
                expectedOrderConfirmMileageDifferenceBetweenMembershipAndRegular={
                  estimatedOrderResponse.expectedOrderConfirmMileageDifferenceBetweenMembershipAndRegular
                }
              />
              <StyledDivider />
              {/* 주문 내용 확인 및 결제 동의 & 결제하기 버튼 섹션 */}
              <OrderPaymentSection amount={estimatedOrderResponse.amount} isSubmitting={isSubmitting} />
            </>
          </form>
        </FormProvider>
      )}
      <OrderErrorModals error={error} isOpen={!!error} onClick={handleClickErrorModalOk} />
    </UserContainer.Provider>
  )
}

const StyledDivider = styled.div`
  height: 0.8rem;
  background-color: ${GlobalColorEnum.Gray70};
`

// todo payco iframeYn 미지원으로 임시 pushState, 추후 제거 20220801 bale
const StyledPaycoBack = styled.div`
  width: 100%;
  height: 80%;
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: ${(props) => props.theme.color.gray900};
  font-weight: 400;
`
